import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import LayoutWrapper from '../components/Layout/LayoutWrapper';
import { LoadingSpinner, ErrorHandler } from '../components/common/ErrorHandler';
import {
  Users,
  UserCircle,
  Gamepad2,
  Wallet,
  TrendingUp,
  TrendingDown,
  DollarSign,
  PiggyBank,
  Activity,
  RefreshCw
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ComposedChart,
  Line
} from 'recharts';

// Inline styles for animations
const styles = `
  .fade-in {
    animation: fadeIn 0.5s ease-in;
  }
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  .card-hover {
    transition: all 0.3s ease;
  }
  
  .card-hover:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
  }

  .progress-bar {
    transition: width 0.5s ease;
  }

  .pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: .5; }
  }
`;

const resolveProfileImage = (img) => {
  if (!img) return null;
  if (img.startsWith('http')) return img;
  return `${process.env.REACT_APP_BACKEND_URL}${img}`;
};
const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchDashboardStats();

    // Update time every second
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    // Auto-refresh data every 30 seconds
    const dataInterval = setInterval(() => {
      fetchDashboardStats(true);
    }, 30000);

    return () => {
      clearInterval(timeInterval);
      clearInterval(dataInterval);
    };
  }, []);

  const fetchDashboardStats = async (isAutoRefresh = false) => {
    try {
      if (!isAutoRefresh) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      const response = await api.get('/api/dashboard/stats');
      setStats(response.data);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleManualRefresh = () => {
    fetchDashboardStats(true);
  };

  const formatDateTime = (date) => {
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <LayoutWrapper title="Dashboard">
        <LoadingSpinner text="Loading dashboard..." />
      </LayoutWrapper>
    );
  }

  if (error) {
    return (
      <LayoutWrapper title="Dashboard">
        <ErrorHandler error={error} onRetry={fetchDashboardStats} />
      </LayoutWrapper>
    );
  }

  const statCards = [
    {
      title: 'Active Games',
      value: stats?.operations?.active_games || 0,
      icon: Gamepad2,
      gradient: 'from-purple-600 to-purple-700',
      iconBg: 'bg-white/20',
      description: 'Running now'
    },
    {
      title: 'Total Members',
      value: stats?.operations?.total_members || 0,
      icon: Users,
      gradient: 'from-blue-600 to-blue-700',
      iconBg: 'bg-white/20',
      description: `${stats?.operations?.active_members || 0} active`
    },
    {
      title: "Kitty Profits",
      value: `₹${(stats?.financials?.kitty_profits || 0).toLocaleString()}`,
      icon: TrendingUp,
      gradient: 'from-green-600 to-green-700',
      iconBg: 'bg-white/20',
      description: 'Total earnings'
    },
    {
      title: 'Total Expenses',
      value: `₹${(stats?.financials?.total_expenses || 0).toLocaleString()}`,
      icon: TrendingDown,
      gradient: 'from-red-600 to-red-700',
      iconBg: 'bg-white/20',
      description: 'Including salaries'
    },
    {
      title: 'Cash Balance',
      value: `₹${(stats?.financials?.cash_balance || 0).toLocaleString()}`,
      icon: Wallet,
      gradient: 'from-amber-600 to-amber-700',
      iconBg: 'bg-white/20',
      description: 'Available'
    },
    {
      title: 'Net Profit',
      value: `₹${(stats?.financials?.net_profit || 0).toLocaleString()}`,
      icon: Activity,
      gradient: (stats?.financials?.net_profit || 0) >= 0 ? 'from-green-600 to-green-700' : 'from-red-600 to-red-700',
      iconBg: 'bg-white/20',
      description: 'After expenses'
    }
  ];

  // Weekly Kitty Movement Data
  const weeklyKittyData = stats?.weekly_kitty || [
    { day: 'Sun', kitty: 0 },
    { day: 'Mon', kitty: 0 },
    { day: 'Tue', kitty: 0 },
    { day: 'Wed', kitty: 0 },
    { day: 'Thu', kitty: 0 },
    { day: 'Fri', kitty: 0 },
    { day: 'Sat', kitty: 0 }
  ];

  const currentKitty = stats?.financials?.kitty_profits || 0;
  const previousKitty = weeklyKittyData[weeklyKittyData.length - 2]?.kitty || 0;
  const kittyChange = currentKitty - previousKitty;
  const kittyChangePercent = previousKitty > 0 ? ((kittyChange / previousKitty) * 100).toFixed(2) : '0.00';

  // Profit & Expense Trend Data (using daily_trends)
  const profitExpenseTrendData = stats?.daily_trends || [
    { date: '01/01', games: 0, kitty: 0, expenses: 0, net: 0 }
  ];

  // Map daily_trends to chart format
  const chartData = profitExpenseTrendData.map(item => ({
    day: item.date,
    kitty: item.kitty,
    expenses: item.expenses,
    profit: item.net
  }));

  // Performance Radar Data
  const performanceData = [
    {
      metric: 'Revenue',
      value: Math.min(100, ((stats?.financials?.kitty_profits || 0) / 15000) * 100),
      fullMark: 100
    },
    {
      metric: 'Growth',
      value: Math.min(100, ((stats?.operations?.active_members || 0) / (stats?.operations?.total_members || 1)) * 100),
      fullMark: 100
    },
    {
      metric: 'Games',
      value: Math.min(100, ((stats?.operations?.active_games || 0) / 10) * 100),
      fullMark: 100
    },
    {
      metric: 'Cash',
      value: Math.min(100, ((stats?.financials?.cash_balance || 0) / 300000) * 100),
      fullMark: 100
    },
    {
      metric: 'Profit',
      value: stats?.financials?.kitty_profits > 0 
        ? Math.min(100, ((stats?.financials?.net_profit || 0) / stats?.financials?.kitty_profits) * 100)
        : 0,
      fullMark: 100
    }
  ];

  // Revenue Distribution (Pie)
  const pieData = [
    { name: 'Kitty Profits', value: stats?.financials?.kitty_profits || 0, color: '#10B981' },
    { name: 'Expenses', value: stats?.financials?.total_expenses || 0, color: '#EF4444' }
  ];

  // Transaction Analysis (Bar Chart) - using daily_trends
  const transactionData = profitExpenseTrendData.map(item => ({
    day: item.date,
    credits: item.kitty,
    debits: item.expenses
  }));

  // Top 5 Winners
  const topWinners = stats?.top_winners || [];

  const maxPoints = topWinners.length > 0 
    ? Math.max(...topWinners.map(w => Math.abs(w.net_winloss)), 50)
    : 50;

  return (
    <LayoutWrapper
      title="Master Dashboard"
      subtitle="Real-time Operations & Financial Overview"
    >
      <div className="space-y-6 fade-in" data-testid="dashboard">
        <style>{styles}</style>

        {/* Header: Live Date/Time & Refresh */}
        <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="text-white">
              <span className="text-sm text-gray-400">Live Date/Time:</span>
              <span className="ml-2 text-lg font-semibold text-green-400" data-testid="live-datetime">
                {formatDateTime(currentTime)}
              </span>
            </div>
            {refreshing && <span className="text-xs text-yellow-400 pulse">Refreshing...</span>}
          </div>
          <button
            onClick={handleManualRefresh}
            disabled={refreshing}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg transition-all shadow-lg"
            data-testid="refresh-button"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>

        {/* Stats Grid (6 Cards) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {statCards.map((card, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br ${card.gradient} rounded-xl p-6 shadow-lg card-hover`}
              data-testid={`stat-card-${index}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">{card.title}</p>
                  <h3 className="text-4xl font-bold text-white mt-2">{card.value}</h3>
                  <p className="text-white/70 text-xs mt-1">{card.description}</p>
                </div>
                <div className={`w-14 h-14 ${card.iconBg} rounded-lg flex items-center justify-center`}>
                  <card.icon className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Weekly Kitty Movement */}
        <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">Weekly Kitty Movement</h3>
            <div className="text-right">
              <div className="text-2xl font-bold text-white">₹{currentKitty.toLocaleString()}</div>
              <div className={`text-sm font-semibold flex items-center justify-end ${kittyChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {kittyChange >= 0 ? '↑' : '↓'} ₹{Math.abs(kittyChange).toLocaleString()}
                <span className="ml-1">({kittyChange >= 0 ? '+' : ''}{kittyChangePercent}%)</span>
              </div>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={weeklyKittyData}>
                <defs>
                  <linearGradient id="kittyGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={kittyChange >= 0 ? "#10B981" : "#EF4444"} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={kittyChange >= 0 ? "#10B981" : "#EF4444"} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="day" stroke="#9ca3af" fontSize={12} />
                <YAxis stroke="#9ca3af" fontSize={12} />
                <Tooltip
                  formatter={(value) => `₹${value.toLocaleString()}`}
                  contentStyle={{
                    backgroundColor: '#1f2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    color: '#fff'
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="kitty"
                  stroke={kittyChange >= 0 ? "#10B981" : "#EF4444"}
                  fillOpacity={1}
                  fill="url(#kittyGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Profit & Expense Trend */}
        <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-5">
          <h3 className="text-lg font-bold text-white mb-4">Income vs Expense (Last 7 Days)</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData}>
                <defs>
                  <linearGradient id="kittyArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.6}/>
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="expensesArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#EF4444" stopOpacity={0.6}/>
                    <stop offset="95%" stopColor="#EF4444" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="day" stroke="#9ca3af" fontSize={12} />
                <YAxis stroke="#9ca3af" fontSize={12} />
                <Tooltip
                  formatter={(value) => `₹${value.toLocaleString()}`}
                  contentStyle={{
                    backgroundColor: '#1f2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    color: '#fff'
                  }}
                />
                <Legend wrapperStyle={{ color: '#fff' }} />
                <Area
                  type="monotone"
                  dataKey="kitty"
                  fill="url(#kittyArea)"
                  stroke="#10B981"
                  name="Kitty"
                />
                <Area
                  type="monotone"
                  dataKey="expenses"
                  fill="url(#expensesArea)"
                  stroke="#EF4444"
                  name="Expenses"
                />
                <Line
                  type="monotone"
                  dataKey="profit"
                  stroke="#3B82F6"
                  strokeWidth={3}
                  dot={{ fill: '#3B82F6', r: 4 }}
                  name="Net Profit"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Charts Row: Radar + Pie + Bar */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Performance Radar */}
          <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-5">
            <h3 className="text-lg font-bold text-white mb-4">System Health Score</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={performanceData}>
                  <PolarGrid stroke="#374151" />
                  <PolarAngleAxis dataKey="metric" stroke="#9ca3af" fontSize={12} />
                  <PolarRadiusAxis stroke="#9ca3af" fontSize={10} />
                  <Radar
                    name="Performance"
                    dataKey="value"
                    stroke="#8B5CF6"
                    fill="#8B5CF6"
                    fillOpacity={0.6}
                  />
                  <Tooltip
                    formatter={(value) => `${value.toFixed(1)}%`}
                    contentStyle={{
                      backgroundColor: '#1f2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Revenue Distribution (Pie) */}
          <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-5">
            <h3 className="text-lg font-bold text-white mb-4">Income vs Expense</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    labelLine={{ stroke: '#9ca3af' }}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => `₹${value.toLocaleString()}`}
                    contentStyle={{
                      backgroundColor: '#1f2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Transaction Analysis (Bar) */}
          <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-5">
            <h3 className="text-lg font-bold text-white mb-4">Daily Cash Flow</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={transactionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="day" stroke="#9ca3af" fontSize={12} />
                  <YAxis stroke="#9ca3af" fontSize={12} />
                  <Tooltip
                    formatter={(value) => `₹${value.toLocaleString()}`}
                    contentStyle={{
                      backgroundColor: '#1f2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                  <Legend wrapperStyle={{ color: '#fff' }} />
                  <Bar dataKey="credits" fill="#10B981" name="Income" />
                  <Bar dataKey="debits" fill="#EF4444" name="Expenses" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Top 5 Winners Today */}
        {topWinners.length > 0 && (
          <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-5">
            <h3 className="text-lg font-bold text-white mb-4">Top Winners Today</h3>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="top-winners-table">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">#</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">PLAYER</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">PLAYER ID</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">NET WIN/LOSS</th>
                  </tr>
                </thead>
                <tbody>
                  {topWinners.map((winner, index) => (
                    <tr key={index} className="border-b border-gray-700 hover:bg-gray-700/50 transition-colors">
                      <td className="py-3 px-4 text-white font-bold">{index + 1}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-3">
                          {winner.profile_picture ? (
                            <img 
                              src={resolveProfileImage(winner.profile_picture)}
                              alt={winner.player_name}
                              className="w-8 h-8 rounded-full object-cover border-2 border-gray-600"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center">
                              <UserCircle className="w-8 h-8 text-gray-400" />
                            </div>
                          )}
                          <span className="text-white">{winner.player_name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-400">{winner.player_id}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-3">
                          <div className="flex-1 bg-gray-700 rounded-full h-3 overflow-hidden">
                            <div
                              className={`progress-bar h-full ${
                                winner.net_winloss >= 0 
                                  ? 'bg-gradient-to-r from-green-500 to-emerald-400' 
                                  : 'bg-gradient-to-r from-red-500 to-red-400'
                              }`}
                              style={{ width: `${(Math.abs(winner.net_winloss) / maxPoints) * 100}%` }}
                            />
                          </div>
                          <span className={`font-bold text-sm min-w-[4rem] text-right ${
                            winner.net_winloss >= 0 ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {winner.net_winloss >= 0 ? '+' : ''}{winner.net_winloss}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-5">
          <h3 className="text-lg font-bold text-white mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <button
              onClick={() => navigate('/members/add')}
              className="flex flex-col items-center justify-center p-4 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1 duration-200"
              data-testid="quick-add-member"
            >
              <Users className="w-8 h-8 text-white mb-2" />
              <span className="text-sm font-semibold text-white">Add Member</span>
            </button>
            <button
              onClick={() => navigate('/kitty/create')}
              className="flex flex-col items-center justify-center p-4 bg-gradient-to-r from-purple-600 to-purple-700 rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1 duration-200"
              data-testid="quick-create-table"
            >
              <Gamepad2 className="w-8 h-8 text-white mb-2" />
              <span className="text-sm font-semibold text-white">Create Table</span>
            </button>
            <button
              onClick={() => navigate('/cash-counter')}
              className="flex flex-col items-center justify-center p-4 bg-gradient-to-r from-green-600 to-green-700 rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1 duration-200"
              data-testid="quick-cash-counter"
            >
              <Wallet className="w-8 h-8 text-white mb-2" />
              <span className="text-sm font-semibold text-white">Cash Counter</span>
            </button>
            <button
              onClick={() => navigate('/petty-cash')}
              className="flex flex-col items-center justify-center p-4 bg-gradient-to-r from-amber-600 to-amber-700 rounded-lg hover:from-amber-700 hover:to-amber-800 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1 duration-200"
              data-testid="quick-petty-cash"
            >
              <DollarSign className="w-8 h-8 text-white mb-2" />
              <span className="text-sm font-semibold text-white">Petty Cash</span>
            </button>
          </div>
        </div>
      </div>
    </LayoutWrapper>
  );
};

export default Dashboard;